<template>
  <div class="home-account-status" data-testid="home-account-status">
    <div class="home-account-status__wrapper">
      <div class="home-account-status__container">
        <Heading
          class="home-account-status__title"
          data-testid="account-status-title"
          :level="2"
        >
          {{ accountStatusResponse.accountStatus.title }}
        </Heading>
        <BaseText
          class="home-account-status__message"
          :color="NebraskaColors.elementAlpha"
          size="p4"
          data-testid="account-status-message"
        >
          {{ accountStatusResponse.accountStatus.description }}
        </BaseText>
        <ButtonPrimary
          v-if="button"
          class="home-account-status__button"
          :label="button?.label || 'Continuar'"
          :href="buttonHref"
          applied-on="blur"
          size="md"
        />
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { getDomainAndPlatform } from '@/modules/common/helpers/onboarding/getDomainAndPlatform';
import {
  AccountStates,
  EAccountStatusAction
} from '@/modules/common/services/http/account';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import { BaseText, ButtonPrimary, Heading } from '@warrenbrasil/nebraska-web';
import { computed, onMounted, ref } from 'vue';

const props = defineProps({
  accountStatusResponse: {
    type: Object as () => AccountStates,
    required: true
  }
});
const storeHref = ref('');
const button = props.accountStatusResponse.accountStatus.button;

function mountStoreHref() {
  const currentUrl = window.location.href;
  const baseUrl = currentUrl.split('/#/')[0];
  storeHref.value = `${baseUrl}/#/mobile`;
}

const isMobile = ref(false);

function checkIfMobile() {
  isMobile.value = window.innerWidth <= 768;
}

onMounted(() => {
  checkIfMobile();
});

const routeMapping: Record<
  EAccountStatusAction,
  { mobile: string; desktop: string; withHost?: boolean }
> = {
  [EAccountStatusAction.FacematchRejected]: {
    mobile: 'states/face-match',
    desktop: 'states/face-match'
  },
  [EAccountStatusAction.FacematchRequired]: {
    mobile: 'states/face-match',
    desktop: 'states/face-match'
  },
  [EAccountStatusAction.FacematchError]: {
    mobile: 'states/facematch-error',
    desktop: 'states/facematch-error'
  },
  [EAccountStatusAction.RegisterAddressReview]: {
    mobile: 'states/register-address-review',
    desktop: 'states/register-address-review'
  },
  [EAccountStatusAction.RegisterDocuments]: {
    mobile: 'register-documents/document',
    desktop: 'documents-info'
  },
  [EAccountStatusAction.RegisterRejected]: {
    mobile: 'states/register-rejected',
    desktop: 'states/register-rejected'
  },
  [EAccountStatusAction.RegisterAnalysis]: {
    mobile: 'states/register-analysis',
    desktop: 'states/register-analysis'
  },
  [EAccountStatusAction.RegisterFinancialLife]: {
    mobile: 'financial-life/profession',
    desktop: 'financial-info'
  },
  [EAccountStatusAction.RegisterPersonal]: {
    mobile: 'personal-info/nationality',
    desktop: 'personal-info'
  },
  [EAccountStatusAction.RegisterMinorsPending]: {
    mobile:
      'https://faq.warren.com.br/hc/pt-br/articles/215877547-Posso-abrir-conta-para-o-meu-filho-Existe-idade-m%C3%ADnima',
    desktop:
      'https://faq.warren.com.br/hc/pt-br/articles/215877547-Posso-abrir-conta-para-o-meu-filho-Existe-idade-m%C3%ADnima',
    withHost: true
  }
};

const buttonHref = computed(() => {
  const { domain, platform } = getDomainAndPlatform();

  if (button?.action) {
    const route = routeMapping[button?.action][platform];
    return routeMapping[button?.action].withHost
      ? route
      : `/onboarding/${domain}/${platform}/${route}`;
  }

  return '';
});

onMounted(() => {
  mountStoreHref();
});
</script>
<style lang="less" scoped>
.home-account-status {
  .container-width(0, @size-spacing-x1200);
  width: 100%;

  .p3 {
    font-weight: 600;
  }

  &__wrapper {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  }

  &__container {
    display: flex;
    flex-flow: column;
    gap: @size-spacing-x400;
    color: @text-over-image;
    max-width: 492px;
  }

  &__button {
    margin-top: @size-spacing-x800;
    max-width: 175px;

    :deep(.button--container) {
      font-weight: 400;
    }
  }

  &__title {
    color: @text-over-image;
  }

  &__qrcode {
    display: flex;
    gap: @size-spacing-x500;
    height: fit-content;
    padding: @size-spacing-x600;
    background: rgba(250, 248, 248, 0.15);
    backdrop-filter: blur(9px) saturate(1.5) brightness(1.05);
    -webkit-backdrop-filter: blur(9px) saturate(1.5) brightness(1.05);
    border-radius: @size-radius-x400;

    &--wrapper {
      display: flex;
      gap: @size-spacing-x400;
      flex-direction: column;
    }

    &--steps {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    &--step {
      display: flex;
      align-items: flex-start;
      gap: @size-spacing-x350;
      max-width: 204px;
    }

    &--canvas {
      padding: @size-spacing-x300;
      background: @element-over-image;
      height: 142px;
      display: block;
    }
  }

  @media (max-height: 650px) {
    margin-top: @size-spacing-x600;
  }

  @media (max-width: 1024px) {
    &__wrapper {
      flex-direction: column;
      gap: @size-spacing-x600;
    }

    &__qrcode {
      width: fit-content;

      &--canvas {
        display: none;
      }
    }
  }
}
</style>
